/**
 * Notificações no aparelho — opt-in de push do próprio usuário.
 *
 * Página, e não item de menu, por causa do iOS: quando o push não está
 * disponível a resposta útil não é "indisponível", é uma instrução de como
 * instalar o app na tela de início. Isso não cabe num toggle.
 *
 * A ordem das operações importa. O backend só é atualizado DEPOIS que o
 * navegador concedeu a permissão — nunca antes. Gravar o opt-in primeiro
 * deixaria o banco dizendo "quer receber" para quem recusou o prompt, e o
 * alerta sairia para um destinatário que não existe. Preferência que mente é
 * o mesmo defeito do toggle do keepalive, de novo.
 */

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing, BellOff, Share, Plus, Smartphone } from "lucide-react";
import { toast } from "react-hot-toast";

import { authApi } from "@/api/auth";
import { apiErrorMessage } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { useTenants } from "@/hooks/queries";
import type { PushPrefs, PushPrefsUpdate } from "@/types";

import { Card, CardBody, CardHeader } from "@/components/Card";
import { Button } from "@/components/Button";
import { Field, Select } from "@/components/Field";
import { PageHeader } from "@/components/PageHeader";
import {
  ativarPush,
  desativarPush,
  prepararPush,
  disponibilidade,
  permissaoAtual,
  type MotivoIndisponivel,
} from "@/lib/push";

const PISOS: { valor: PushPrefs["push_severity_floor"]; label: string; ajuda: string }[] = [
  { valor: "info", label: "Info (tudo)", ajuda: "Todo alerta, inclusive informativo." },
  { valor: "warning", label: "Warning para cima", ajuda: "O padrão. Deixa de fora o informativo." },
  { valor: "critical", label: "Critical para cima", ajuda: "Só o que exige ação." },
  // v36 — quem só quer ser acordado quando a carga está em risco.
  { valor: "disaster", label: "Só Disaster", ajuda: "Só quando o problema já dura tempo demais." },
];

export function NotificacoesPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const ehStaff = user?.role === "super_admin";

  const prefs = useQuery({
    queryKey: ["push-prefs"],
    queryFn: () => authApi.pushPrefs(),
  });

  const tenants = useTenants(ehStaff);

  // Disponibilidade e permissão dependem do estado do NAVEGADOR, que muda
  // fora do React (a pessoa instala o PWA, revoga a permissão nas
  // configurações). Recalcula na montagem em vez de confiar no cache.
  const [disp, setDisp] = useState(() => disponibilidade());
  const [permissao, setPermissao] = useState<NotificationPermission>(() => permissaoAtual());
  useEffect(() => {
    setDisp(disponibilidade());
    setPermissao(permissaoAtual());
  }, []);

  const [ativando, setAtivando] = useState(false);
  // "pronto" = SDK carregado, inicializado e com o external_id amarrado.
  //
  // Isto roda na MONTAGEM, não no clique, e é requisito do iOS: o pedido de
  // permissão precisa sair dentro da ativação transiente do gesto, e ela não
  // sobrevive a um await de rede. Preparar aqui deixa o clique livre para
  // chamar requestPermission() direto.
  const [pronto, setPronto] = useState(false);
  const [erroPreparo, setErroPreparo] = useState<string | null>(null);

  useEffect(() => {
    if (!disp.ok || !user) return;
    let vivo = true;
    prepararPush(user.id)
      .then(() => vivo && setPronto(true))
      .catch((e: Error) => {
        console.error("[push] preparo falhou", e);
        if (vivo) setErroPreparo(e.message);
      });
    return () => {
      vivo = false;
    };
  }, [disp.ok, user]);

  const salvar = useMutation({
    mutationFn: (patch: PushPrefsUpdate) => authApi.updatePushPrefs(patch),
    onSuccess: (nova) => {
      qc.setQueryData(["push-prefs"], nova);
      toast.success("Preferência salva");
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  async function ligar() {
    if (!user) return;
    setAtivando(true);
    try {
      const r = await ativarPush();
      setPermissao(permissaoAtual());
      if (r === "nao-pronto") {
        toast.error("Ainda preparando. Tente de novo em um instante.");
        return;
      }
      if (r === "negado") {
        toast.error(
          "O navegador negou a permissão. Libere nas configurações do site e tente de novo.",
        );
        return;
      }
      if (r === "erro") {
        toast.error("Não consegui ativar agora. Tente novamente em instantes.");
        return;
      }
      // Só agora o backend fica sabendo — permissão concedida de verdade.
      salvar.mutate({ push_enabled: true });
    } finally {
      setAtivando(false);
    }
  }

  async function desligar() {
    await desativarPush();
    salvar.mutate({ push_enabled: false });
  }

  const p = prefs.data;
  const ligado = Boolean(p?.push_enabled);
  const faltaEscolherCliente = ehStaff && !p?.push_tenant_id;

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader
        title="Notificações no aparelho"
        description="Receba os alertas como notificação push, neste aparelho."
      />

      {!disp.ok && <Indisponivel motivo={disp.motivo!} />}

      {disp.ok && (
        <Card className="mt-4">
          <CardHeader title="Push" />
          <CardBody className="space-y-5">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-sm font-medium text-slate-900 dark:text-slate-100">
                  {ligado ? (
                    <BellRing className="h-4 w-4 text-brand-500" />
                  ) : (
                    <BellOff className="h-4 w-4 text-slate-400" />
                  )}
                  {ligado ? "Ativado neste aparelho" : "Desativado"}
                </div>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Vale só para este aparelho e para você. Não altera os canais
                  de e-mail e WhatsApp configurados pelo cliente.
                </p>
              </div>
              <Button
                size="sm"
                variant={ligado ? "ghost" : "primary"}
                disabled={
                  ativando ||
                  prefs.isLoading ||
                  (!ligado && !pronto) ||
                  (!ligado && faltaEscolherCliente)
                }
                onClick={ligado ? desligar : ligar}
              >
                {ativando
                  ? "Ativando…"
                  : ligado
                    ? "Desativar"
                    : pronto
                      ? "Ativar"
                      : "Preparando…"}
              </Button>
            </div>

            {erroPreparo && (
              <p className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-700 dark:text-red-300">
                Não consegui carregar o serviço de notificações: {erroPreparo}.
                Recarregue a página; se persistir, é bloqueio de rede ou CSP.
              </p>
            )}

            {permissao === "denied" && !ligado && (
              <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-300">
                Este navegador está com a permissão de notificação{" "}
                <strong>bloqueada</strong> para o site. Depois de negar, ele não
                pergunta de novo — libere no cadeado da barra de endereço e
                volte aqui.
              </p>
            )}

            {ehStaff && (
              <Field
                label="Cliente que você acompanha"
                hint="Staff recebe push de um cliente por vez. Sem escolher, não chega nada."
              >
                <Select
                  value={p?.push_tenant_id ?? ""}
                  options={[
                    { value: "", label: "— nenhum —" },
                    ...(tenants.data ?? []).map((t) => ({
                      value: t.id,
                      label: t.name,
                    })),
                  ]}
                  onChange={(e) =>
                    salvar.mutate({ push_tenant_id: e.target.value || null })
                  }
                />
              </Field>
            )}

            <Field
              label="A partir de qual severidade"
              hint={PISOS.find((x) => x.valor === p?.push_severity_floor)?.ajuda}
            >
              <Select
                value={p?.push_severity_floor ?? "warning"}
                options={PISOS.map((x) => ({ value: x.valor, label: x.label }))}
                onChange={(e) =>
                  salvar.mutate({
                    push_severity_floor: e.target
                      .value as PushPrefs["push_severity_floor"],
                  })
                }
              />
            </Field>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

function Indisponivel({ motivo }: { motivo: MotivoIndisponivel }) {
  if (motivo === "ios-em-aba") {
    return (
      <Card className="mt-4">
        <CardHeader title="Instale o app primeiro" />
        <CardBody className="space-y-3 text-sm text-slate-600 dark:text-slate-300">
          <p>
            No iPhone e no iPad, notificação só funciona com o app instalado na
            tela de início. É limitação do próprio iOS, não do envpulse.
          </p>
          <ol className="space-y-2 text-sm">
            <li className="flex items-center gap-2">
              <Share className="h-4 w-4 flex-none text-brand-500" />
              Toque em <strong>Compartilhar</strong>, na barra do Safari
            </li>
            <li className="flex items-center gap-2">
              <Plus className="h-4 w-4 flex-none text-brand-500" />
              Escolha <strong>Adicionar à Tela de Início</strong>
            </li>
            <li className="flex items-center gap-2">
              <Smartphone className="h-4 w-4 flex-none text-brand-500" />
              Abra o envpulse por esse ícone e volte a esta tela
            </li>
          </ol>
        </CardBody>
      </Card>
    );
  }

  if (motivo === "sem-app-id") {
    return (
      <Card className="mt-4">
        <CardBody className="text-sm text-slate-600 dark:text-slate-300">
          Push não está configurado neste ambiente.
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="mt-4">
      <CardBody className="text-sm text-slate-600 dark:text-slate-300">
        Este navegador não suporta notificações push. Em computador, o Chrome,
        o Edge e o Firefox funcionam; no celular, instale o app na tela de
        início.
      </CardBody>
    </Card>
  );
}
